import type { LatLngTuple } from 'leaflet';
import type { MoTravelDay, MoTravelStop } from '../types/moTravel.types';
import {
  getStopLabel,
  getStopTitle,
  isStartWaypoint,
  type DayRoute,
  type RouteWaypoint,
  type WaypointKind,
} from './dayRoutes';
import { getStartKey, getStopKey } from './stopKey';

type JourneyEntryKind = WaypointKind | 'unlocated';

export interface JourneyEntry {
  key: string;
  day: MoTravelDay;
  dayColor: string;
  kind: JourneyEntryKind;
  label: string;
  title: string;
  stop: MoTravelStop | null;
}

function toStartEntry(route: DayRoute, waypointByKey: Map<string, RouteWaypoint>): JourneyEntry[] {
  const startWaypoint = waypointByKey.get(getStartKey(route.day));
  if (!startWaypoint) return [];
  return [
    {
      key: startWaypoint.key,
      day: route.day,
      dayColor: route.color,
      kind: 'start',
      label: startWaypoint.label,
      title: startWaypoint.title,
      stop: null,
    },
  ];
}

function toStopEntries(route: DayRoute, waypointByKey: Map<string, RouteWaypoint>): JourneyEntry[] {
  return route.day.stops.map((stop) => {
    const key = getStopKey(route.day, stop);
    return {
      key,
      day: route.day,
      dayColor: route.color,
      kind: waypointByKey.get(key)?.kind ?? 'unlocated',
      label: getStopLabel(stop),
      title: getStopTitle(stop),
      stop,
    };
  });
}

export function buildJourneyEntries(routes: DayRoute[]): JourneyEntry[] {
  return routes.flatMap((route) => {
    const waypointByKey = new Map(route.waypoints.map((waypoint) => [waypoint.key, waypoint]));
    return [...toStartEntry(route, waypointByKey), ...toStopEntries(route, waypointByKey)];
  });
}

export const countJourneyStops = (entries: JourneyEntry[]): number =>
  entries.filter((entry) => !isStartWaypoint(entry)).length;

function getLegPoints(route: DayRoute, index: number): LatLngTuple[] | null {
  const waypoint = route.waypoints[index];
  if (!waypoint || isStartWaypoint(waypoint)) return null;
  return route.legPaths[index - 1] ?? [waypoint.position];
}

export function buildLegPointsByWaypointKey(
  routes: DayRoute[],
): ReadonlyMap<string, LatLngTuple[] | null> {
  return new Map(
    routes.flatMap((route) =>
      route.waypoints.map((waypoint, index) => [waypoint.key, getLegPoints(route, index)]),
    ),
  );
}
